import React from 'react';
import { v4 as uuid } from 'uuid';
import { addDays, differenceInDays, differenceInMinutes, isSameDay } from 'date-fns';
import { Page, useExecutiveCheckupRouter } from '../../router/context';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import { ProductSlotsTemplate } from '../../../../templates/ProductSlotsTemplate';
import { SlotHour, fetchDateSlots, fetchSlotsByDate } from '../../../../modules/slots';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { daysInMonth } from '../../../../utils/dates';
import { DateSlots } from '../../../../templates/ProductSlotsTemplate/components/ProductSlots';
import { toast } from 'react-toastify';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { useScheduledItems } from '../../store/ScheduledItems';

type ProductSlotsPageProps = {
  isFirstAppointment?: boolean;
  dateOfFirstAppointment?: Date;
};

export function ProductSlotsPage({ isFirstAppointment, dateOfFirstAppointment }: Readonly<ProductSlotsPageProps>) {
  const { navigate, goBack } = useExecutiveCheckupRouter();
  const { selectedService: service } = useServiceContext();
  const { changeSchedule, selectedItem: schedule } = useScheduleContext();

  const { scheduledItems: itemsAlreadyScheduled } = useScheduledItems();

  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = React.useState(false);

  const selectedProduct = React.useMemo(() => {
    return schedule && service.products.find(product => product.id === schedule.productId);
  }, [schedule]);

  const loadSlotsDates = async (baseDate: Date): Promise<DateSlots[]> => {
    let startDate = addDays(new Date(), 1);

    if (dateOfFirstAppointment) {
      const firstAvailableDate = new Date(dateOfFirstAppointment);
      if (baseDate <= firstAvailableDate) startDate = firstAvailableDate;
    }

    if (baseDate.getMonth() > startDate.getMonth() || baseDate.getFullYear() > startDate.getFullYear()) {
      startDate.setDate(1);
      startDate.setMonth(baseDate.getMonth());
      startDate.setFullYear(baseDate.getFullYear());
    }
    const daysOnMonth = daysInMonth(baseDate.getMonth() + 1, baseDate.getFullYear());
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), daysOnMonth);

    if (differenceInDays(endDate, startDate) < 4) endDate.setDate(endDate.getDate() + 5);

    try {
      const response = await fetchDateSlots({
        startDate: startDate.toISOString().substring(0, 10),
        endDate: endDate.toISOString().substring(0, 10),
        isKit: schedule?.isKit ?? false,
        itemId: schedule?.itemId ?? '',
        unitId: schedule?.unitId ?? '',
        localId: schedule?.localId ?? ''
      });

      return response.slotDates.map((item: string) => ({ id: uuid(), date: item } as DateSlots));
    } catch (error: any) {
      console.log('error', error);
      setIsModalSystemUnavailableVisible(true);
    }
    return [];
  };

  const isDateGreaterOrEqual24Hours = (date: Date): boolean => date > addDays(new Date(), 1);

  const excludeHoursLess24Hours = (hours: SlotHour[]): SlotHour[] =>
    hours.filter(hour => isDateGreaterOrEqual24Hours(new Date(`${hour.date}T${hour.hour}`)));

  const excludeHoursOfOthersSchedules = (hours: SlotHour[]): SlotHour[] => {
    const APPOINTMENTS_INTERVAL = Number(process.env.REACT_APP_EXECUTIVE_CHECKUP_SCHEDULED_INTERVAL);

    return hours.filter(hour => {
      const dateCheck = new Date(`${hour.date}T${hour.hour}`);

      let isDateInRange = false;

      itemsAlreadyScheduled.forEach(item => {
        const dateToFilter = new Date(item);

        if (isSameDay(dateToFilter, dateCheck)) {
          const difference = differenceInMinutes(dateToFilter, dateCheck);

          if (difference >= -APPOINTMENTS_INTERVAL && difference <= APPOINTMENTS_INTERVAL) isDateInRange = true;
        }
      });

      if (isDateInRange) return false;
      return true;
    });
  };

  const loadSlotsHours = async (date: Date): Promise<DateSlots[]> => {
    try {
      if (schedule) {
        const response = await fetchSlotsByDate({
          date: date.toISOString().substring(0, 10),
          isKit: schedule?.isKit ?? false,
          itemId: schedule?.itemId ?? '',
          unitId: schedule?.unitId ?? '',
          localId: schedule?.localId ?? ''
        });

        const filteredDates = excludeHoursOfOthersSchedules(response);
        const datesGreater24Hours = excludeHoursLess24Hours(filteredDates);

        return datesGreater24Hours.map(
          item => ({ id: uuid(), date: `${item.date}T${item.hour}`, slot: item } as DateSlots)
        );
      }
      return [];
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível consultar slots.');
    }
    return [];
  };

  const submit = async (slotSelected: DateSlots) => {
    const scheduleFactory = ScheduleItemPropsBuilder();
    scheduleFactory.setTime({
      slots: [slotSelected.slot],
      time: new Date(slotSelected.date),
      ...slotSelected
    });

    changeSchedule({ ...schedule, ...scheduleFactory.setSlotsOptions([slotSelected.slot]) });

    navigate(Page.APPOINTMENT_CONFIRMATION, { isFirstAppointment });
  };

  return (
    <>
      <ProductSlotsTemplate
        headTitle="Escolha uma data e horário"
        goBack={goBack}
        productName={selectedProduct?.name ?? ''}
        productId={selectedProduct?.id ?? ''}
        handleSlot={submit}
        loadSlotsDates={loadSlotsDates}
        loadSlotsHours={loadSlotsHours}
        serviceName="Check-up"
      />

      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />
    </>
  );
}
